/**
 * Die Tabelle der Seite "Aktivitaeten": je protokollierter Aenderung eine Zeile mit den Spalten
 * Wann, Wer, Wo, Was, Vorher -> Nachher und Quelle. Die Texte der Werte (`altAnzeige`/`neuAnzeige`)
 * und die Quelle leitet der Server ab; die Tabelle zeigt sie nur (kein zweiter Formatierer im
 * Frontend, siehe `api/aktivitaeten.ts`). Die wenigen Faelle, die der Server nicht kennen kann (Altzeilen,
 * Rollenschluessel, alt = neu), und der Text der Spalte "Wo" stehen als reine Funktionen in `aktivitaetenAnzeige.ts`.
 *
 * KLICK AUF DEN ORT (Phase 2, Task 11B: `klickZiel`): eine anklickbare Zeile traegt in der Spalte "Wo" einen ECHTEN
 * Knopf (Tastatur erreichbar, Enter/Leertaste), und die ganze Zeile reagiert auf den Mausklick. Beides
 * laeuft ueber EINEN Handler an der Zeile - der Knopf hat keinen eigenen `onClick`, sein Klick (auch
 * der von der Tastatur synthetisierte) steigt zur Zeile auf. So wird nie doppelt geoeffnet. Das Ziel ist je nach Art
 * der Drawer einer Bestellung (`onBestellungOeffnen`) oder eine Seite (`onSeiteOeffnen`: Nutzerverwaltung, Lieferant,
 * Zahlungsvorlagen, Zahlungen); die Rechte der Zielseiten prueft `klickZiel`.
 *
 * Eine nicht oeffenbare BESTELLUNG ist entweder ueber den Hub geloescht (`wo.geloescht`; das Backend haengt "(gelöscht)"
 * an die Bezeichnung, die Zeile ist ohne Knopf und ohne Klick) oder in JTL geloescht (`darfBestellungOeffnen`, ohne
 * Loescheintrag): dann steht der Hinweis "In JTL gelöscht". Eine Sendung ohne Bestellung (`art === "sendung"`) ist
 * ebenfalls nicht oeffenbar, aber keine geloeschte Bestellung - sie bekommt den Hinweis nicht.
 */
import type { JSX } from "react";
import { QUELLEN_LABEL, type Aktivitaet, type AktivitaetWo } from "../../api/aktivitaeten.ts";
import { fmtDT } from "../../lib/format.ts";
import { aenderungsAnzeige, klickHinweis, klickZiel, woText, type KlickZiel, type SeitenZiel } from "./aktivitaetenAnzeige.ts";

export interface AktivitaetenTabelleProps {
  eintraege: readonly Aktivitaet[];
  onBestellungOeffnen: (wo: AktivitaetWo) => void;
  /** Klick auf ein Objekt, das eine eigene Seite hat (Nutzer, Lieferant, Zahlungsvorlage, FX). */
  onSeiteOeffnen: (ziel: SeitenZiel) => void;
  /** Die Rechte des angemeldeten Nutzers; sie entscheiden, ob die Zielseite eines Objekts erreichbar ist. */
  rechte: ReadonlySet<string>;
}

function WoZelle({ wo, ziel }: { wo: AktivitaetWo; ziel: KlickZiel | null }): JSX.Element {
  const name = woText(wo);
  return (
    <td className="akt-wo">
      {ziel ? (
        <button type="button" className="akt-wo-knopf" title={klickHinweis(ziel)}>
          {name}
        </button>
      ) : (
        <span className="akt-wo-name">{name}</span>
      )}
      {wo.art === "bestellung" && !wo.oeffenbar && !wo.geloescht ? <span className="akt-geloescht">In JTL gelöscht</span> : null}
      {wo.lieferant ? <div className="akt-wo-lieferant">{wo.lieferant}</div> : null}
    </td>
  );
}

function AenderungsZelle({ eintrag }: { eintrag: Aktivitaet }): JSX.Element {
  const anzeige = aenderungsAnzeige(eintrag);
  // Ein Satz statt Werten: Freitextzeile ("Bestellung angelegt"), Altzeile, Kennung ohne Aenderung.
  if (anzeige.art === "satz") {
    return (
      <td className="akt-aenderung">
        <span className="akt-summary">{anzeige.text}</span>
      </td>
    );
  }
  // Ein JSON-Wert ist in der Zelle nur zusammengefasst; der Rohwert steht im Tooltip.
  const titel =
    eintrag.feldTyp === "json" ? `${eintrag.altWert ?? "leer"} → ${eintrag.neuWert ?? "leer"}` : undefined;
  return (
    <td className="akt-aenderung" title={titel}>
      {anzeige.alt === null ? (
        <span className="akt-alt akt-leer">leer</span>
      ) : (
        <del className="akt-alt">{anzeige.alt}</del>
      )}
      <span className="akt-pfeil">→</span>
      <strong className={anzeige.neu === null ? "akt-neu akt-leer" : "akt-neu"}>
        {anzeige.neu === null ? "leer" : anzeige.neu}
      </strong>
    </td>
  );
}

export function AktivitaetenTabelle({
  eintraege,
  onBestellungOeffnen,
  onSeiteOeffnen,
  rechte,
}: AktivitaetenTabelleProps): JSX.Element {
  const oeffnen = (eintrag: Aktivitaet, ziel: KlickZiel): void => {
    if (ziel.art === "bestellung") onBestellungOeffnen(eintrag.wo);
    else onSeiteOeffnen(ziel);
  };
  return (
    <div className="akt-tabelle-wrap">
      <table className="ov akt-tabelle" aria-label="Protokollierte Änderungen">
        <thead>
          <tr>
            <th scope="col">Wann</th>
            <th scope="col">Wer</th>
            <th scope="col">Wo</th>
            <th scope="col">Was</th>
            <th scope="col">Vorher → Nachher</th>
            <th scope="col">Quelle</th>
          </tr>
        </thead>
        <tbody>
          {eintraege.map((eintrag) => {
            const ziel = klickZiel(eintrag.wo, rechte);
            return (
              <tr
                key={eintrag.kennung}
                className={ziel ? "akt-zeile akt-oeffenbar" : "akt-zeile"}
                onClick={ziel ? () => oeffnen(eintrag, ziel) : undefined}
              >
                <td className="akt-wann">{fmtDT(eintrag.ts)}</td>
                <td className={eintrag.aktorArt === "automatisch" ? "akt-wer akt-automatisch" : "akt-wer"}>
                  {eintrag.aktor}
                </td>
                <WoZelle wo={eintrag.wo} ziel={ziel} />
                <td className="akt-was">{eintrag.feldLabel ?? "—"}</td>
                <AenderungsZelle eintrag={eintrag} />
                <td className="akt-quelle">{QUELLEN_LABEL[eintrag.quelle] ?? eintrag.quelle}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
